import React, { useState } from 'react'
import { Col, Container, Row } from 'react-grid-system'
import { useForm } from 'react-hook-form'
import { TailSpin } from 'react-loader-spinner'

import Checkbox from '../../../../shared/components/Checkbox/Checkbox'
import Input from '../../../../shared/components/Input/Input'
import SuccessModal from '../../../../shared/components/SuccessModal/SuccessModal'
import { submitToZoho } from '../../../../utils/api'
import { type ContactFormCopy } from '../../types'
import styles from './styles.module.scss'

const Form = ({
  afterSubmit,
  copy,
}: {
  afterSubmit?: () => void
  copy: Required<ContactFormCopy>
}) => {
  const [isModalOpen, setIsModalOpen] = useState(false)
  const {
    register,
    formState: { errors },
    handleSubmit,
    reset,
    getValues,
    setValue,
  } = useForm()
  const [isLoad, setIsLoad] = useState(false)

  const onSubmit = async () => {
    setIsLoad(true)
    const first = getValues('firstName')
    const last = getValues('lastName')
    const email = getValues('mail')
    const phone = getValues('phone')
    const orgName = getValues('org')
    const textArea = getValues('textArea')
    const service = getValues('service')

    const description = [textArea, service ? `Service of Interest: ${service}` : '']
      .filter(Boolean)
      .join('\n')

    try {
      await submitToZoho({
        company: orgName,
        firstName: first,
        lastName: last,
        ...(email ? { email } : {}),
        ...(phone ? { phone } : {}),
        ...(description ? { description } : {}),
      })
      reset()
      setIsModalOpen(true)
      afterSubmit?.()
    } finally {
      setIsLoad(false)
    }
  }

  return (
    <div className={styles.wrapper}>
      <Container>
        <Row>
          <Col lg={6} className={styles.form_image_container}>
            <img
              className={styles.form_image}
              src={require('../../../../../public/images/hp_form.webp')}
              alt="Urus Advisory team member speaking with a client"
            />
          </Col>
          <Col lg={6} className={styles.rightContainer}>
            <Container
              style={{
                paddingRight: 0,
                maxWidth: 1200,
              }}
              className={styles.form_content_container}
            >
              <Row
                style={{
                  maxWidth: '100vw',
                }}
              >
                <Col xs={12} className={styles.col_wrapper}>
                  <div className={styles.form_content}>
                    <h3>{copy.title}</h3>
                    <p>{copy.description}</p>
                  </div>
                </Col>
              </Row>
            </Container>

            <form
              style={{}}
              onSubmit={(e) => {
                e.preventDefault()
                void handleSubmit(onSubmit)()
              }}
            >
              <Container>
                <Row className={styles.formFields}>
                  <Col sm={6} className={`${styles.section} ${styles.firstNameField}`}>
                    <Input
                      name="firstName"
                      register={register}
                      rest={{
                        disabled: isLoad,
                        style: {
                          border: errors.firstName ? '1px solid #ff000073' : '1px solid #E3D0C9',
                          background: errors.firstName ? '#f2dbdbf0' : '#ffff',
                          marginBottom: 0,
                        },
                        placeholder: copy.firstName,
                      }}
                    />
                  </Col>
                  <Col sm={6} className={`${styles.section} ${styles.lastNameField}`}>
                    <Input
                      register={register}
                      name="lastName"
                      rest={{
                        disabled: isLoad,
                        style: {
                          border: errors.lastName ? '1px solid #ff000073' : '1px solid #E3D0C9',
                          background: errors.lastName ? '#f2dbdbf0' : '#ffff',
                          marginBottom: 0,
                        },
                        placeholder: copy.lastName,
                      }}
                    />
                  </Col>
                  <Col sm={6} className={`${styles.section} ${styles.emailField}`}>
                    <Input
                      register={register}
                      pattern={{
                        value: /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i,
                        message: 'invalid email address',
                      }}
                      name="mail"
                      rest={{
                        disabled: isLoad,
                        style: {
                          border: errors.mail ? '1px solid #ff000073' : '1px solid #E3D0C9',
                          background: errors.mail ? '#f2dbdbf0' : '#ffff',
                          marginBottom: 0,
                        },
                        placeholder: copy.email,
                      }}
                    />
                  </Col>
                  <Col sm={6} className={`${styles.section} ${styles.phoneField}`}>
                    <Input
                      register={register}
                      name="phone"
                      required={false}
                      pattern={{
                        value: /^\+?[0-9\s().-]{7,}$/,
                        message: 'invalid phone',
                      }}
                      rest={{
                        disabled: isLoad,
                        placeholder: copy.phone,
                        style: {
                          border: errors.phone ? '1px solid #ff000073' : '1px solid #E3D0C9',
                          background: errors.phone ? '#f2dbdbf0' : '#ffff',
                          marginBottom: 0,
                        },
                      }}
                    />
                  </Col>
                  <Col sm={6} className={`${styles.section} ${styles.organisationField}`}>
                    <Input
                      register={register}
                      name="org"
                      rest={{
                        disabled: isLoad,
                        style: {
                          border: errors.org ? '1px solid #ff000073' : '1px solid #E3D0C9',
                          background: errors.org ? '#f2dbdbf0' : '#ffff',
                          marginBottom: 0,
                        },
                        placeholder: copy.organisation,
                      }}
                    />
                  </Col>
                  <Col sm={6} className={`${styles.section} ${styles.serviceField}`}>
                    <Input
                      register={register}
                      name="service"
                      defaultValue={''}
                      isDropdown
                      dropdownItems={[
                        'Integrity Due Diligence & Background Screening',
                        'Asset Tracing',
                        'Corporate Investigations',
                        'Strategic and Geopolitical Risk Advisory',
                        'Supply Chains and Sanctions Compliance',
                      ]}
                      onChange={(val: string) => {
                        setValue('service', val)
                      }}
                      rest={{
                        disabled: isLoad,
                        style: {
                          border: errors.service ? '1px solid #ff000073' : '1px solid #E3D0C9',
                          background: errors.service ? '#f2dbdbf0' : '#ffff',
                          marginBottom: 0,
                        },
                        placeholder: copy.service,
                      }}
                    />
                  </Col>
                </Row>
                <Row>
                  <Col>
                    <Input
                      name="textArea"
                      register={register}
                      required={false}
                      multiline
                      rest={{
                        disabled: isLoad,
                        placeholder: copy.message,
                        style: {
                          border: '1px solid #E3D0C9',
                        },
                      }}
                    />
                  </Col>
                </Row>
                <Row>
                  <Col>
                    <br />
                    <Checkbox
                      id={'chx-1'}
                      isError={!!errors.checkbox1}
                      register={register}
                      name={'checkbox1'}
                      required={false}
                      label={<p className={styles.checkbox_label}>{copy.newsletter}</p>}
                    />
                    <br />
                    <Checkbox
                      id={'chx-2'}
                      isError={!!errors.checkbox2}
                      register={register}
                      name={'checkbox2'}
                      label={
                        <p className={styles.checkbox_label}>
                          Submitting this form confirms your agreement to{' '}
                          <a href="/privacy" target="_blank">
                            Urus
                            <br /> Advisory Privacy Policy
                          </a>{' '}
                          and{' '}
                          <a href="/cookies" target="_blank">
                            Cookies Policy
                          </a>
                          .
                        </p>
                      }
                    />
                    <button>
                      {copy.submit}
                      {isLoad && (
                        <TailSpin
                          // visible={false}
                          height="25"
                          width="25"
                          color="#fff"
                          ariaLabel="line-wave-loading"
                          wrapperClass={styles.loader}
                          wrapperStyle={{}}
                        />
                      )}
                    </button>
                  </Col>
                </Row>
              </Container>
            </form>
          </Col>
        </Row>
      </Container>
      <SuccessModal isOpen={isModalOpen} handleModal={(val: boolean) => setIsModalOpen(val)} />
    </div>
  )
}

export default Form
